import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import cn from 'classnames';
import {
  getFaculty,
  getFacultyDepartments,
  getFacultyEquipment,
  getFacultyPersonnel,
  getFacultyRetraining,
} from '../../actions/faculties';
import Main from './tabs/Main';
import Departments from './tabs/Departments';
import Personnel from './tabs/Personnel';
import Retraining from './tabs/Retraining';
import Equipment from './tabs/Equipment';
import styles from './card.module.css';

/* Карточка факультета — окно поверх списка, только просмотр: кнопки
   «Сохранить» в нём нет, единственное действие — «Закрыть».

   Вкладки грузятся по требованию (п. 4.7): при открытии запрашивается только
   «Главная», остальные — при первом переходе. Загруженное лежит в store до
   закрытия окна и повторно не запрашивается; год вкладки «Переподготовка и ПК»
   кешируется отдельным ключом — у каждого года свои значения. */

const TABS = [
  { key: 'main', label: 'Главная' },
  { key: 'departments', label: 'Подчинённые подразделения' },
  { key: 'personnel', label: 'Штат' },
  { key: 'retraining', label: 'Переподготовка и ПК' },
  { key: 'equipment', label: 'Материально-технические средства' },
];

// счётчик записей в пилюле есть только у вкладок с таблицами
const COUNTED = ['departments', 'personnel', 'equipment'];

const requestOf = (tab, id, year) => {
  if (tab === 'departments') return getFacultyDepartments(id);
  if (tab === 'personnel') return getFacultyPersonnel(id);
  if (tab === 'retraining') return getFacultyRetraining(id, year);
  if (tab === 'equipment') return getFacultyEquipment(id);
  return getFaculty(id);
};

const keyOf = (tab, year) => (tab === 'retraining' ? `retraining:${year || ''}` : tab);

const Card = ({ id, onClose }) => {
  const dispatch = useDispatch();

  const [store, setStore] = useState({});
  const [tab, setTab] = useState('main');
  // null — год не выбирали: сервер сам вернёт текущий вместе с перечнем лет.
  // В состояние год попадает только по выбору пользователя — иначе подстановка
  // пришедшего года меняла бы ключ кеша и вызывала повторный запрос тех же данных.
  const [year, setYear] = useState(null);

  // окно живо и открыто на этой записи: ответ, пришедший после закрытия окна
  // или после смены записи, в состояние не пишется
  const aliveRef = useRef(true);
  const idRef = useRef(id);

  useEffect(() => () => { aliveRef.current = false; }, []);

  // другая запись — окно начинается заново: вкладка «Главная», пустой кеш
  useEffect(() => {
    idRef.current = id;
    setStore({});
    setTab('main');
    setYear(null);
  }, [id]);

  const key = keyOf(tab, year);
  const entry = store[key];

  useEffect(() => {
    if (entry) return;
    setStore(prev => ({ ...prev, [key]: { status: 'loading' } }));
    (async () => {
      const result = await dispatch(requestOf(tab, id, year));
      if (!aliveRef.current || idRef.current !== id) return;
      setStore(prev => ({
        ...prev,
        [key]: result.error
          ? { status: 'error', error: result.error, code: result.status }
          : { status: 'ok', data: result.data },
      }));
    })();
  }, [dispatch, entry, key, tab, id, year]);

  // Отказ по правам на МТС — вкладку скрываем, а не показываем ошибку (п. 9.2)
  const equipmentDenied = store.equipment
    && store.equipment.status === 'error'
    && store.equipment.code === 403;

  useEffect(() => {
    if (equipmentDenied && tab === 'equipment') setTab('main');
  }, [equipmentDenied, tab]);

  // пока окно открыто, страница под ним не прокручивается; ширина исчезнувшего
  // скроллбара компенсируется отступом, чтобы страница не дёрнулась вправо
  useEffect(() => {
    const { body } = document;
    const gap = window.innerWidth - document.documentElement.clientWidth;
    const prevOverflow = body.style.overflow;
    const prevPadding = body.style.paddingRight;
    body.style.overflow = 'hidden';
    if (gap > 0) body.style.paddingRight = `${gap}px`;
    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPadding;
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const counterOf = useCallback((tabKey) => {
    const loaded = store[tabKey];
    return loaded && loaded.status === 'ok' ? loaded.data.count : null;
  }, [store]);

  const main = store.main && store.main.status === 'ok' ? store.main.data : null;

  // перечень лет одинаков для всех годов, поэтому берётся из любого уже
  // полученного ответа вкладки — так список лет не пропадает на время загрузки
  const retrainingYears = Object.keys(store)
    .filter(item => item.indexOf('retraining:') === 0)
    .map(item => store[item].data && store[item].data.years)
    .find(Boolean) || [];

  const renderBody = () => {
    if (!entry || entry.status === 'loading') {
      if (tab === 'retraining' && retrainingYears.length) {
        return (
          <Retraining
            years={retrainingYears}
            year={year}
            plans={null}
            loading
            onYearChange={setYear}
          />
        );
      }
      return (
        <div className={styles.loader}>
          <Spinner animation="border" role="status">
            <span className="sr-only">Loading...</span>
          </Spinner>
        </div>
      );
    }

    if (entry.status === 'error') {
      return (
        <div className={styles.error}>
          <div className={styles.errorTitle}>{entry.error}</div>
          {entry.code === 403
            ? 'Записи этого факультета вам недоступны'
            : 'Данные не получены'}
        </div>
      );
    }

    if (tab === 'main') return <Main data={entry.data} />;
    if (tab === 'departments') return <Departments data={entry.data} />;
    if (tab === 'personnel') return <Personnel data={entry.data} />;
    if (tab === 'equipment') return <Equipment data={entry.data} />;
    return (
      <Retraining
        years={entry.data.years}
        year={year == null ? entry.data.year : year}
        plans={entry.data}
        loading={false}
        onYearChange={setYear}
      />
    );
  };

  const subtitle = main
    ? [
      main.shortname && `Сокращение: ${main.shortname}`,
      main.prefix_code && `Код: ${main.prefix_code}`,
      main.parent_name,
    ].filter(Boolean).join('  ·  ')
    : '';

  return createPortal(
    <div
      className={styles.sheetOv}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={styles.sheet}>
        <div className={styles.sheetTitle}>
          <h2>{main ? main.name : 'Факультет'}</h2>
          {!!subtitle && <div className={styles.sheetSub}>{subtitle}</div>}
        </div>

        <div className={styles.tabs}>
          {TABS
            .filter(item => !(item.key === 'equipment' && equipmentDenied))
            .map((item) => {
              const count = COUNTED.indexOf(item.key) >= 0 ? counterOf(item.key) : null;
              return (
                <button
                  key={item.key}
                  type="button"
                  className={cn({ [styles.tabActive]: item.key === tab })}
                  onClick={() => setTab(item.key)}
                >
                  {item.label}
                  {count != null && <span className={styles.tabCount}>{count}</span>}
                </button>
              );
            })}
        </div>

        <div className={styles.sheetBody}>{renderBody()}</div>

        <div className={styles.sheetFoot}>
          <button type="button" onClick={onClose}>Закрыть</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Card;
